/**
 * Letterhead designs — authored via the `/pack` toile process (plan
 * `thinking/plans/2026_08_31_toile_pack_wiring.md` Track B), wave `toile_office_formats`. Narcis file-split
 * shape (Track A): one module per pack, `registry.ts` assembles them into `livePacks`.
 */
import {tagline, chips, type ToilePack} from './data'

export const letterheadDesignsPack: ToilePack = {
    slug: 'letterhead-designs',
    category: 'office',
    name: 'Letterhead designs',
    tagline: tagline('pieces', 'themes', 'styles'),
    chips: chips('themes', 'styles'),
    ratio: {w: 832, h: 1216},
    readings: [
        {key: 'corporate', label: 'Corporate', subject: 'a letterhead with a logo placeholder top left and a coloured footer band'},
        {key: 'doctor', label: 'Doctor', subject: 'a doctor\'s letterhead with a caduceus and "Dr." name line'},
        {key: 'lawyer', label: 'Lawyer', subject: "a lawyer's letterhead with scales"},
    ],
    styles: [
        {key: 'minimal', label: 'Minimal', style: 'minimal letterhead with a small logo mark at the top, a hairline rule and a one-line footer', palette: 'black ink and grey on white', framing: 'single page seen straight on, centered on a plain light grey ground with an even white margin around the sheet'},
        {key: 'band', label: 'Colour band', style: 'letterhead with a bold colour band across the top edge and a slim matching band at the foot', palette: 'deep blue and white', framing: 'single page seen straight on, centered on a plain light grey ground with an even white margin around the sheet'},
        {key: 'classic', label: 'Classic', style: 'classic professional letterhead with centered name, thin double rule and small serif contact line', palette: 'burgundy and dark grey on cream', framing: 'single page seen straight on, centered on a plain light grey ground with an even white margin around the sheet'},
    ],
    directions: ['gold line', 'round logo', 'green'],
    directionHint: 'try: gold line',
    seo: {
        title: 'Letterhead design: corporate, doctor, lawyer',
        metaDescription: "Nine printable letterhead designs: a corporate, a doctor's and a lawyer's letterhead, each in a minimal, colour band or classic style.",
        h1: 'Letterhead design: corporate, doctor, lawyer',
        tagline: 'Three letterheads, each in three styles, with the body left as grey lines for your letter.',
        intro: "A letterhead design is the printed top and foot of a page of business paper. These nine show a corporate letterhead with a logo placeholder; a doctor's letterhead with a caduceus; and a lawyer's letterhead with scales. Only the Address and Phone headings are lettered. Type a twist, such as gold line, and it is a request.",
        useCases: [
            "a clinic's letters to patients",
            "a law firm's letters to clients",
            "a company's everyday business letters",
            'a printed page for a home office',
        ],
        faq: [
            {q: 'Is the logo included?', a: 'The corporate page has a logo placeholder, so you place your own mark there. The doctor and lawyer pages show a caduceus and scales as drawn symbols.'},
            {q: 'Can I print it on my own paper?', a: 'The page is a flat, print-ready design with an even margin, so it can be printed at full page size.'},
            {q: 'What are the three styles?', a: 'Minimal is black and grey with a small logo mark and a one-line footer. Colour band is deep blue and white with a band at the top and foot. Classic is burgundy and dark grey on cream, with a centred name and a thin double rule.'},
            {q: 'Can I add a gold line or a round logo?', a: 'Type it as a twist, such as gold line or round logo. It is a request for that look, not a promised result.'},
        ],
    },
}
